import clsx from 'clsx'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import type { ImageChangeHandler, ImageLoadHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import { useAppSelector } from 'hooks/reduxHooks'
import type { PropsWithChildren, ReactElement } from 'react'
import styled from 'styled-components'
import { selectLoggedIn } from 'selectors/accountSelectors'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useImageCarouselHoverPreview from 'hooks/useImageCarouselHoverPreview'

const Container = styled.div`
  grid-area: hero;
`

interface Props extends PropsWithChildren {
  bookmarkAction?: ReactElement
  images: Array<App.Image>
  productLabel?: ReactElement | false
  bottomLeft?: ReactElement
  imageParams: ImageParams
  /** First carousel image eager + fetchPriority high; other slides lazy-managed (BaseImageCarousel). */
  eagerLoadFirstImage?: boolean
  onImageChange?: ImageChangeHandler
  onImageLoad?: ImageLoadHandler
  className?: string
}

function SearchImageCarouselWrapper(props: Props) {
  const {
    bookmarkAction,
    images,
    productLabel,
    bottomLeft,
    imageParams,
    eagerLoadFirstImage,
    onImageChange,
    onImageLoad,
    className,
  } = props
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hideBookmark = isMetaSearchMobileV2 && !isLoggedIn
  const isDesktop = useScreenSizeUp('tablet')
  const { carouselRef, handleImageChange, handleMouseEnter, handleMouseLeave } = useImageCarouselHoverPreview(images, isDesktop, onImageChange)

  return <Container
    className={clsx(SearchImageCarouselWrapper.name, className)}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
  >
    <ImageCarousel
      carouselRef={carouselRef}
      topLeft={productLabel}
      topRight={bookmarkAction && !hideBookmark ? <WishlistGuard>{bookmarkAction}</WishlistGuard> : undefined}
      bottomLeft={bottomLeft}
      images={images}
      arrowBehaviour="interaction-visible"
      imageParams={imageParams}
      eagerLoadFirstImage={eagerLoadFirstImage}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
  </Container>
}

export default SearchImageCarouselWrapper
